"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, CaretDownIcon } from "@phosphor-icons/react/ssr";
import clsx from "clsx";
import countriesData from "@/lib/countries.json";

// Use countries from the JSON file and transform to match expected format
const countries = countriesData
    .filter((country) => country.dialCode && country.dialCode !== "")
    .map((country) => ({
        id: `${country.dialCode}-${country.name}`, // Unique identifier
        code: country.dialCode,
        name: country.name,
        flag: country.emoji,
    }));

// Remove duplicates and sort by name
const uniqueCountries = countries
    .filter(
        (country, index, self) =>
            index === self.findIndex((c) => c.id === country.id)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

interface PhoneCountrySelectProps {
    value?: string; // This will be the dial code (e.g., "+1")
    onChange?: (value: string) => void; // This will receive the dial code
    className?: string;
    disabled?: boolean;
    error?: boolean;
}

export function PhoneCountrySelect({
    value = "+47",
    onChange,
    className,
    disabled = false,
    error = false,
}: PhoneCountrySelectProps) {
    const t = useTranslations();
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCountryId, setSelectedCountryId] = useState<string>("");
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Find the selected country based on the dial code
    // If multiple countries have the same code, use the first one or the previously selected one
    const selectedCountry =
        uniqueCountries.find(
            (country) =>
                country.code === value &&
                (selectedCountryId === "" || country.id === selectedCountryId)
        ) ||
        uniqueCountries.find((country) => country.code === value) ||
        uniqueCountries[0];

    const filteredCountries =
        query === ""
            ? uniqueCountries.sort((a, b) => {
                  // Extract numeric part of the code for proper sorting
                  const aNum = parseInt(a.code.replace(/[^0-9]/g, ""));
                  const bNum = parseInt(b.code.replace(/[^0-9]/g, ""));
                  return aNum - bNum;
              })
            : uniqueCountries
                  .filter(
                      (country) =>
                          country.name
                              .toLowerCase()
                              .includes(query.toLowerCase()) ||
                          country.code.includes(query)
                  )
                  .sort((a, b) => {
                      // Extract numeric part of the code for proper sorting
                      const aNum = parseInt(a.code.replace(/[^0-9]/g, ""));
                      const bNum = parseInt(b.code.replace(/[^0-9]/g, ""));
                      return aNum - bNum;
                  });

    const handleSelect = (countryId: string) => {
        const selectedCountry = uniqueCountries.find(
            (country) => country.id === countryId
        );
        if (selectedCountry) {
            setSelectedCountryId(countryId);
            onChange?.(selectedCountry.code);
        }
        setIsOpen(false);
        setQuery("");
    };

    useEffect(() => {
        if (!isOpen) {
            setQuery("");
        }
    }, [isOpen]);

    // Initialize selectedCountryId when value changes
    useEffect(() => {
        if (value && selectedCountryId === "") {
            const country = uniqueCountries.find((c) => c.code === value);
            if (country) {
                setSelectedCountryId(country.id);
            }
        }
    }, [value, selectedCountryId]);

    return (
        <div className={clsx("relative", className)}>
            <Listbox
                value={selectedCountry.id}
                onChange={handleSelect}
                disabled={disabled}
            >
                {({ open }) => (
                    <div>
                        <ListboxButton
                            ref={buttonRef}
                            aria-label={t("common.selectCountry")}
                            className={clsx(
                                "relative w-full cursor-default appearance-none bg-transparent py-1.5 pr-7 pl-3 text-base text-zinc-500 placeholder:text-zinc-400 focus:outline-none sm:text-sm/6 dark:bg-transparent dark:text-zinc-400 dark:placeholder:text-zinc-500",
                                error ? "ring-red-300" : "ring-zinc-300"
                            )}
                            onClick={() => setIsOpen(!open)}
                        >
                            <div className="flex items-center">
                                <span className="text-lg mr-2">
                                    {selectedCountry.flag}
                                </span>
                                <span className="block truncate font-medium text-zinc-900 dark:text-zinc-400">
                                    {selectedCountry.code}
                                </span>
                            </div>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <CaretDownIcon
                                    className={clsx(
                                        "size-5 text-zinc-500 sm:size-4 dark:text-zinc-400 transition-transform duration-200",
                                        open && "rotate-180"
                                    )}
                                    aria-hidden="true"
                                />
                            </span>
                        </ListboxButton>

                        <ListboxOptions
                            anchor="bottom start"
                            transition
                            className="z-50 mt-1 max-h-60 w-[--button-width] overflow-hidden rounded-md bg-white dark:bg-zinc-800 text-base shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 focus:outline-none sm:text-sm [--anchor-gap:4px] transition duration-100 ease-in data-[closed]:opacity-0"
                        >
                            <div className="sticky top-0 z-20 bg-white dark:bg-zinc-800 px-3 py-2 border-b border-zinc-200 dark:border-zinc-700">
                                <input
                                    type="text"
                                    placeholder={t("common.search")}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    aria-label={t("common.search")}
                                    className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-400"
                                />
                            </div>
                            <div className="max-h-48 overflow-auto">
                                {filteredCountries.length === 0 ? (
                                    <div className="relative cursor-default select-none px-4 py-2 text-zinc-700 dark:text-zinc-300">
                                        {t("common.noCountriesFound")}
                                    </div>
                                ) : (
                                    filteredCountries.map((country) => (
                                        <ListboxOption
                                            key={country.id}
                                            className="relative cursor-default select-none py-2 pl-3 pr-9 data-[active]:bg-blue-500/10 dark:data-[active]:bg-blue-500/20 data-[active]:text-zinc-500 text-zinc-900 dark:text-white"
                                            value={country.id}
                                        >
                                            {({ selected }) => (
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <span className="text-lg mr-3">
                                                            {country.flag}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <span className="text-sm text-zinc-500 dark:text-zinc-400 mr-2">
                                                            {country.code}
                                                        </span>
                                                        {selected && (
                                                            <CheckIcon
                                                                className="h-5 w-5 text-blue-600 dark:text-blue-400"
                                                                aria-hidden="true"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </ListboxOption>
                                    ))
                                )}
                            </div>
                        </ListboxOptions>
                    </div>
                )}
            </Listbox>
        </div>
    );
}
